// Shared test account, hardcoded on the client and available in production too.
//
// The user store is in-memory, so every protected route (/test, /payment,
// /intensive) bounces back to "/" on a direct link or a plain refresh. Signing
// in as this user fills the store from the constant below instead of the
// backend, so those guards pass on any deployment.
//
// Two ways in:
//   * type tested@test.local into the e-mail popup on the landing
//   * append ?testUser=1 to any route — ?testUser=0 signs out again
//
// The session is kept in sessionStorage, so it survives refreshes and direct
// links for the rest of the tab.
//
// Nothing about this user reaches the backend: the lookup, the create call and
// the test-result POST are all skipped. Checkout is the exception — PaymentStage
// still calls the real payment endpoint, so paying as this user on production
// creates a real Monobank invoice.

export const TEST_USER_EMAIL = "tested@test.local";

const STORAGE_KEY = "testUser";
const OFF_VALUES = new Set(["0", "off", "false", "no"]);

// Same shape GET /api/intensive/user returns. testResult 12 is the B1B2 band;
// isPaid false keeps the funnel pointed at /payment, not /successful-payment.
function buildTestUser() {
  return {
    email: TEST_USER_EMAIL,
    testResult: 12,
    isPaid: false,
    lastPaymentAt: null,
    name: null,
    phoneNumber: null,
    tgUsername: null,
    time: null,
  };
}

export function isTestUserEmail(email) {
  return (
    typeof email === "string" && email.trim().toLowerCase() === TEST_USER_EMAIL
  );
}

export function isTestUser(user) {
  return isTestUserEmail(user?.email);
}

function remember() {
  try {
    sessionStorage.setItem(STORAGE_KEY, "1");
  } catch (e) {
    // sessionStorage can throw in a hardened browser profile — the session then
    // lasts until the next full page load instead of the whole tab
  }
}

export function forgetTestUser() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {}
}

function isRemembered() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch (e) {
    return false;
  }
}

// Signs in: call it when the test e-mail is submitted through the funnel.
export function startTestUserSession() {
  remember();
  return buildTestUser();
}

// Runs before the first render (see src/index.js). Returns the user to seed the
// store with, or null when this is an ordinary visitor.
export function bootstrapTestUser() {
  const params = new URLSearchParams(window.location.search);
  if (params.has(STORAGE_KEY)) {
    const value = (params.get(STORAGE_KEY) || "1").trim().toLowerCase();
    if (OFF_VALUES.has(value)) {
      forgetTestUser();
      return null;
    }
    return startTestUserSession();
  }
  return isRemembered() ? buildTestUser() : null;
}
